import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SectionDivider = () => (
  <div className="w-full h-px bg-[#C9A84C]/20" />
);

export default function TermsConditions() {
  useEffect(() => {
    document.title = 'Terms & Conditions | MANDIE OZEE.';
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFAF8]">
      <Navbar />

      <main>
        {/* Page Header */}
        <section className="pt-28 pb-10 md:pt-40 md:pb-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto">
              <p className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-4">
                Legal
              </p>
              <h1 className="font-heading text-xl md:text-3xl lg:text-5xl text-[#1A1A1A] leading-tight mb-3">
                Terms & Conditions
              </h1>
              <p className="font-body text-sm text-[#999999]">
                Last updated: May 15, 2026
              </p>
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* Terms Content */}
        <section className="py-12 md:py-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto font-body text-sm text-[#555555] leading-relaxed space-y-10">

              {/* Agreement */}
              <div>
                <p className="mb-4">
                  Welcome to Mandie Ozee. By accessing or using our website at mandieozee.com (the "Site") and purchasing products from our online shop, you agree to be bound by these Terms & Conditions ("Terms"). If you do not agree to these Terms, please do not use the Site or purchase our products.
                </p>
                <p>
                  These Terms apply to all visitors, users, and customers who access or use the Site. Please read them carefully before placing an order.
                </p>
              </div>

              <SectionDivider />

              {/* Products */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  1. Products and Descriptions
                </h2>
                <p className="mb-4">
                  We strive to display our products, including The H.O.M.E Collection (journal and card deck set) and any future products, as accurately as possible. However, we cannot guarantee that your device's display of colors, textures, or product details will be perfectly accurate.
                </p>
                <p className="mb-4">
                  All product descriptions, images, and pricing are subject to change at any time without notice. We reserve the right to discontinue any product at any time. Any offer for any product or service made on this Site is void where prohibited.
                </p>
                <p>
                  The H.O.M.E Collection and all associated content, including journal prompts, card deck exercises, and therapeutic frameworks, are intended for personal growth and self-reflection purposes. They are not a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of a qualified mental health provider with any questions you may have regarding a medical or psychological condition.
                </p>
              </div>

              <SectionDivider />

              {/* Orders */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  2. Orders and Payment
                </h2>
                <p className="mb-4">
                  By placing an order through the Site, you represent that you are at least 18 years of age and that all information you provide is accurate, complete, and current. You are responsible for maintaining the confidentiality of any account information.
                </p>
                <p className="mb-4">
                  All prices are listed in the currency displayed on the checkout page and are subject to applicable taxes and shipping fees, which will be calculated and displayed before you confirm your order.
                </p>
                <p className="mb-4">
                  We reserve the right to refuse or cancel any order for any reason, including but not limited to: product availability, errors in product or pricing information, suspicion of fraud, or issues identified by our payment processing partners. If your order is cancelled after payment has been processed, we will issue a full refund.
                </p>
                <p>
                  Payment is processed securely through third-party payment gateways. By submitting your payment information, you authorize us (through our payment processors) to charge the total order amount to your selected payment method.
                </p>
              </div>

              <SectionDivider />

              {/* Shipping */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  3. Shipping and Delivery
                </h2>
                <p className="mb-4">
                  We ship physical products to addresses provided at checkout. Shipping times and costs vary based on your location and selected shipping method. Estimated delivery windows are provided at checkout and in your order confirmation email, these are estimates, not guarantees.
                </p>
                <p className="mb-4">
                  We are not responsible for delays caused by customs processing, carrier service disruptions, severe weather, or other circumstances beyond our reasonable control. Risk of loss and title for purchased items pass to you upon delivery to the shipping carrier.
                </p>
                <p>
                  Please ensure your shipping address is complete and accurate. We are not responsible for orders shipped to incorrectly provided addresses. If a package is returned to us due to an incorrect address, we will contact you to arrange reshipment at your expense.
                </p>
              </div>

              <SectionDivider />

              {/* Returns */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  4. Returns and Refunds
                </h2>
                <p className="mb-4">
                  Due to the personal and intimate nature of our products, we have a thoughtful return policy:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-4 text-[#666666]">
                  <li><strong className="text-[#555555]">Damaged or Defective Items:</strong> If your product arrives damaged or contains a manufacturing defect, please contact us within 14 days of delivery at hello@mandieozee.com with photo evidence. We will arrange a replacement or full refund, including shipping costs.</li>
                  <li><strong className="text-[#555555]">Change of Mind:</strong> Unopened and unused products in their original packaging may be returned within 30 days of delivery. Return shipping is the responsibility of the customer. Refunds will be processed within 7–10 business days after we receive and inspect the return.</li>
                  <li><strong className="text-[#555555]">Non-Returnable:</strong> Opened or used products cannot be returned due to the personal nature of the items.</li>
                </ul>
                <p>
                  To initiate a return, please contact us at hello@mandieozee.com with your order number and reason for return. We will provide return instructions and, where applicable, a return shipping label.
                </p>
              </div>

              <SectionDivider />

              {/* Intellectual Property */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  5. Intellectual Property
                </h2>
                <p className="mb-4">
                  All content on the Site, including but not limited to text, images, graphics, logos, product designs, journal content, card deck exercises, the "Healing. Ownership. Mastery. Evolution." framework, brand names, and the "MANDIE OZEE." mark, is the exclusive intellectual property of Mandie Ozee and is protected by applicable copyright, trademark, and intellectual property laws.
                </p>
                <p className="mb-4">
                  You may not reproduce, distribute, modify, create derivative works from, publicly display, or otherwise exploit any content from the Site or our products without our express prior written consent. Purchasing a physical product does not grant you any license to reproduce, resell, or distribute the content contained within.
                </p>
                <p>
                  User-submitted content (such as testimonials or reviews) grants us a non-exclusive, royalty-free, perpetual license to use, reproduce, and display such content on the Site and in marketing materials, unless otherwise agreed in writing.
                </p>
              </div>

              <SectionDivider />

              {/* User Conduct */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  6. User Conduct
                </h2>
                <p>
                  You agree not to use the Site for any unlawful purpose or in violation of these Terms. Prohibited activities include, but are not limited to: attempting to interfere with the Site's security features, transmitting malware or harmful code, scraping or data mining without authorization, impersonating another person, or using the Site to harass, abuse, or harm others. We reserve the right to terminate access to the Site for any violation of these Terms.
                </p>
              </div>

              <SectionDivider />

              {/* Disclaimer */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  7. Disclaimer of Warranties
                </h2>
                <p className="mb-4">
                  The Site and all products are provided on an "as is" and "as available" basis without warranties of any kind, either express or implied. To the fullest extent permitted by law, we disclaim all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
                </p>
                <p>
                  We do not warrant that the Site will be uninterrupted, error-free, secure, or free of viruses or other harmful components. Your use of the Site is at your sole risk.
                </p>
              </div>

              <SectionDivider />

              {/* Limitation of Liability */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  8. Limitation of Liability
                </h2>
                <p>
                  To the maximum extent permitted by applicable law, Mandie Ozee shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of or inability to use the Site or products, even if we have been advised of the possibility of such damages. In no event shall our total liability to you exceed the amount paid by you for the product or service giving rise to the claim.
                </p>
              </div>

              <SectionDivider />

              {/* Indemnification */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  9. Indemnification
                </h2>
                <p>
                  You agree to indemnify, defend, and hold harmless Mandie Ozee and its officers, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable legal fees, arising out of or in any way connected with your violation of these Terms, your use of the Site, or your infringement of any intellectual property or other rights of any third party.
                </p>
              </div>

              <SectionDivider />

              {/* Third Party Links */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  10. Third-Party Links
                </h2>
                <p>
                  The Site may contain links to third-party websites or services, including but not limited to Calendly (booking), payment processors, and social media platforms. We do not control and are not responsible for the content, privacy practices, or terms of any third-party sites. Your use of third-party services is at your own risk and subject to the respective third party's terms and policies.
                </p>
              </div>

              <SectionDivider />

              {/* Governing Law */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  11. Governing Law
                </h2>
                <p>
                  These Terms shall be governed by and construed in accordance with the applicable laws, without regard to conflict of law principles. Any disputes arising out of or relating to these Terms or your use of the Site shall be resolved through negotiation in good faith. If resolution cannot be reached, the dispute shall be subject to the exclusive jurisdiction of the competent courts.
                </p>
              </div>

              <SectionDivider />

              {/* Changes */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  12. Changes to These Terms
                </h2>
                <p>
                  We reserve the right to modify or replace these Terms at any time. Changes will be posted on this page with an updated "Last updated" date. Your continued use of the Site after any changes constitutes acceptance of the new Terms. We encourage you to review these Terms periodically.
                </p>
              </div>

              <SectionDivider />

              {/* Contact */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  13. Contact
                </h2>
                <p className="mb-4">
                  For questions about these Terms & Conditions, please contact us:
                </p>
                <div className="space-y-1 text-[#666666]">
                  <p>Email: hello@mandieozee.com</p>
                  <p>Contact form: mandieozee.com/contact</p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}